/**
 * Re-resolving an already-attached CompanyCam photo (#22142).
 *
 * A CompanyCam photo's `src` is frozen when it's picked (`toCompanyCamPhotoFile`),
 * so a markup added in CompanyCam *after* the import never reaches PaintScout —
 * the estimator keeps seeing the un-annotated image with no way to refresh it.
 * These helpers are the pure half of the fix: work out which upstream photo a
 * file came from, decide what a fresh copy of that photo means for it, and merge
 * the answer back. The dialog does the I/O and the messaging.
 */

import type { QuoteFile } from 'paintscout'
import type { CompanyCamApiPhoto } from '../dialogs/AddFileDialog/AddFileDialog.helpers'

export const COMPANYCAM_PROVIDER = 'companycam'

export type CompanyCamVariant = 'original' | 'annotated'

/** Which upstream photo (and which of its renders) a file tracks. */
export interface CompanyCamPhotoRef {
  photoId: string
  variant: CompanyCamVariant
}

const KEY_PATTERN = /^(.+)-(annotated|original)$/

/**
 * Resolve the upstream photo a file came from.
 *
 * Prefers the persisted `providerId`/`providerVariant`. Files written before
 * #22142 have neither, so it falls back to the `<photoId>-<variant>` key the
 * pickers have always produced. The key is only a fallback because
 * `swapProviderFileInList` deliberately preserves the *target's* key when a
 * provider file is swapped for a different photo — so on a swapped legacy file the
 * key names a photo the file no longer shows, and syncing off it would revert the
 * swap. Nothing can be done for those retroactively; new records carry the id.
 */
export function getCompanyCamPhotoRef(file?: Partial<QuoteFile> | null): CompanyCamPhotoRef | null {
  if (!file || file.provider !== COMPANYCAM_PROVIDER) {
    return null
  }

  const variant = file.providerVariant
  if (file.providerId && (variant === 'annotated' || variant === 'original')) {
    return { photoId: file.providerId, variant }
  }

  const match = typeof file.key === 'string' ? file.key.match(KEY_PATTERN) : null
  if (!match) {
    return null
  }
  return { photoId: match[1], variant: match[2] as CompanyCamVariant }
}

/** Whether the Sync action should be offered for this file at all. */
export function canSyncCompanyCamFile(file?: Partial<QuoteFile> | null): boolean {
  return !!getCompanyCamPhotoRef(file)
}

export type CompanyCamSyncStatus =
  /** CompanyCam has no usable image for this photo — leave the file alone. */
  | 'unavailable'
  /** The variant this file tracks still resolves to the same url. */
  | 'up-to-date'
  /** The variant this file tracks now resolves to a different url. */
  | 'updated'
  /** An annotated file whose markup no longer exists upstream; fell back to the original. */
  | 'markup-removed'

export interface CompanyCamSyncResult {
  status: CompanyCamSyncStatus
  /** The file with its own variant re-resolved. Absent when `status` is 'unavailable'. */
  file?: QuoteFile
  /**
   * True when the photo has a markup this file isn't showing *and* hasn't already
   * been offered. The caller *offers* the switch rather than taking it: the
   * estimator chose the un-annotated render on purpose, and silently overwriting
   * that is its own bug report.
   */
  markupAvailable?: boolean
  /** The file switched over to the markup. Only set alongside `markupAvailable`. */
  annotatedFile?: QuoteFile
  /**
   * The annotation url being offered. Only set alongside `markupAvailable`; the
   * caller writes it back with `rememberDeclinedMarkup` when the user says no, so
   * the same markup is never offered twice.
   */
  markupSrc?: string
  /**
   * Whether the markup is provably new. True when the file recorded a *different*
   * markup before, false when it recorded none at all — a record written before
   * ENG-1118, where "already marked up at import" and "marked up since" are
   * indistinguishable. Only the wording differs; both still offer the switch.
   */
  markupIsNew?: boolean
}

/**
 * The photo's markup render, as a `QuoteFile` field value.
 *
 * CompanyCam sends `null` for an un-annotated photo; `QuoteFile` fields are
 * optional, so the absent case has to be `undefined`. Coerced in one place so
 * the two spellings can't drift apart.
 */
function markupSrcOf(photo: CompanyCamApiPhoto): string | undefined {
  return photo.annotation_url ?? undefined
}

function srcFor(photo: CompanyCamApiPhoto, variant: CompanyCamVariant): string | undefined {
  const src = variant === 'annotated' ? photo.annotation_url ?? photo.url : photo.url
  return src ?? undefined
}

/**
 * Apply a freshly-fetched photo to a file, tracking `variant` from now on.
 *
 * Only the fields that describe *which image this is* are touched. `caption` in
 * particular is left alone: it seeds from the CompanyCam description at import
 * but is editable in the file editor, and a sync must not overwrite what the user
 * typed there.
 */
export function applyCompanyCamSync(file: QuoteFile, photo: CompanyCamApiPhoto, variant: CompanyCamVariant): QuoteFile {
  return {
    ...file,
    src: srcFor(photo, variant),
    provider: COMPANYCAM_PROVIDER,
    providerId: photo.id,
    providerVariant: variant,
    // An annotated file *is* the markup, so it has by definition seen it. An
    // original one keeps whatever it recorded: marking the markup as seen here
    // would silence the offer before the user has answered it.
    providerMarkupSrc: variant === 'annotated' ? markupSrcOf(photo) : file.providerMarkupSrc
  }
}

/**
 * Record a markup the user declined, so Sync stops offering it.
 *
 * Only persists when the user then saves the dialog — the same rule as every
 * other edit in the file editor.
 *
 * An empty `markupSrc` returns the file untouched. Writing `undefined` would
 * clear a markup the user declined earlier, and a cleared record starts
 * prompting again — the bug this whole field exists to stop.
 */
export function rememberDeclinedMarkup(file: QuoteFile, markupSrc?: string): QuoteFile {
  if (!markupSrc) {
    return file
  }
  return { ...file, providerMarkupSrc: markupSrc }
}

/** The fields a sync can rewrite. Everything else on the file is left alone. */
const SYNCED_FIELDS = ['src', 'provider', 'providerId', 'providerVariant', 'providerMarkupSrc'] as const

/**
 * Whether a re-resolve actually changed anything.
 *
 * A sync that finds nothing new must not mark the editor dirty — otherwise
 * clicking Sync on an up-to-date photo makes Cancel warn about unsaved changes
 * the user never made.
 */
export function hasCompanyCamSyncChanges(before: QuoteFile, after: QuoteFile): boolean {
  return SYNCED_FIELDS.some((field) => before[field] !== after[field])
}

/**
 * Decide what a fresh copy of the upstream photo means for `file`.
 *
 * Kept pure and separate from the dialog so the rules — and the wording that
 * hangs off them — are unit-testable.
 */
export function resolveCompanyCamSync(args: { file: QuoteFile; photo: CompanyCamApiPhoto }): CompanyCamSyncResult {
  const { file, photo } = args
  const ref = getCompanyCamPhotoRef(file)
  const variant = ref?.variant ?? 'original'

  const hasMarkup = !!photo.annotation_url
  const markupRemoved = variant === 'annotated' && !hasMarkup

  let effectiveVariant: CompanyCamVariant = variant
  if (markupRemoved) {
    // Its stored annotation url is a render CompanyCam no longer serves, so the
    // only honest thing left to show is the original.
    effectiveVariant = 'original'
  } else if (variant === 'original' && hasMarkup && photo.annotation_url === file.src) {
    // Labelled Original but already showing the markup — possible on records that
    // predate `providerVariant`, where the annotated pick fell back to a key/url
    // pair that disagree. It is de facto tracking the markup; don't revert it.
    effectiveVariant = 'annotated'
  }

  const nextSrc = srcFor(photo, effectiveVariant)
  if (!nextSrc) {
    return { status: 'unavailable' }
  }

  const synced = applyCompanyCamSync(file, photo, effectiveVariant)

  const status: CompanyCamSyncStatus = markupRemoved
    ? 'markup-removed'
    : nextSrc === file.src
      ? 'up-to-date'
      : 'updated'

  // The photo is showing an Original and CompanyCam has a markup the file hasn't
  // been offered yet — the case this issue is about. Hand back both so the caller
  // can refresh in place and then ask whether to switch.
  //
  // The `providerMarkupSrc` comparison is what stops the offer repeating: a photo
  // that was already marked up when the estimator picked its un-annotated render
  // recorded that markup at pick time, so it is not news. Without it, Sync asked
  // "marked up since it was added?" on every click, about a photo nothing had
  // changed about (ENG-1118).
  const markupUnseen = hasMarkup && photo.annotation_url !== file.providerMarkupSrc
  if (effectiveVariant === 'original' && markupUnseen) {
    return {
      status,
      file: synced,
      markupAvailable: true,
      annotatedFile: applyCompanyCamSync(file, photo, 'annotated'),
      markupSrc: markupSrcOf(photo),
      // A recorded-but-different markup is provably new. No record at all means a
      // pre-ENG-1118 file: it gets the offer once, worded without the false claim.
      markupIsNew: !!file.providerMarkupSrc
    }
  }

  return { status, file: synced }
}
